class MegaMenu {
  constructor() {
    this.menus = [...document.querySelectorAll('.mega-menu')];
    this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    this.currentMenu = null;
    this.closeDelayTimer = null;
    this.closeAnimations = new WeakMap();
    this.entryFrames = new WeakMap();

    this.closeDelay = 120;
    this.closeFallbackDuration = 650;

    this.init();
  }

  init() {
    if (window.innerWidth < 990 || !this.menus.length) return;

    this.menus.forEach((menu) => {
      const trigger = menu.querySelector('.mega-menu__trigger');

      if (this.canHover) {
        trigger?.addEventListener('pointerenter', () => this.open(menu));
        trigger?.addEventListener('focus', () => this.open(menu));

        menu.addEventListener('pointerenter', () => {
          this.cancelScheduledClose();
          this.cancelCloseAnimation(menu);
        });

        menu.addEventListener('pointerleave', (event) => {
          if (event.relatedTarget && menu.contains(event.relatedTarget)) return;
          this.scheduleClose(menu);
        });

        menu.addEventListener('focusout', (event) => {
          if (event.relatedTarget && menu.contains(event.relatedTarget)) return;
          this.scheduleClose(menu);
        });
      }

      trigger?.addEventListener('click', (event) => {
        event.preventDefault();

        if (this.currentMenu === menu && menu.classList.contains('is-open')) {
          this.close(menu);
        } else {
          this.open(menu);
        }
      });

    });

    document.addEventListener('pointerdown', (event) => {
      if (!event.target.closest('.mega-menu')) this.close(this.currentMenu);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.currentMenu) return;

      const trigger = this.currentMenu.querySelector('.mega-menu__trigger');
      this.close(this.currentMenu);
      trigger?.focus();
    });
  }

  open(menu) {
    this.cancelScheduledClose();
    this.cancelCloseAnimation(menu);

    if (this.currentMenu === menu && menu.classList.contains('is-open')) {
      menu.querySelector('.mega-menu__trigger')?.setAttribute('aria-expanded', 'true');
      return;
    }

    if (this.currentMenu && this.currentMenu !== menu) {
      this.finishClose(this.currentMenu);
    }

    this.currentMenu = menu;
    menu.classList.add('is-open');

    const trigger = menu.querySelector('.mega-menu__trigger');
    const panel = menu.querySelector('.mega-menu__content');

    trigger?.setAttribute('aria-expanded', 'true');

    if (!panel) return;

    panel.classList.remove('menu-closing', 'animate-items', 'shine');
    this.cancelEntryFrame(menu);

    const frame = requestAnimationFrame(() => {
      this.entryFrames.delete(menu);

      if (this.currentMenu !== menu || !menu.classList.contains('is-open')) return;
      panel.classList.add('animate-items', 'shine');
    });

    this.entryFrames.set(menu, frame);
  }

  close(menu) {
    if (!menu || !menu.classList.contains('is-open')) return;

    this.cancelScheduledClose();
    this.cancelEntryFrame(menu);
    this.cancelCloseAnimation(menu);

    const trigger = menu.querySelector('.mega-menu__trigger');
    const panel = menu.querySelector('.mega-menu__content');

    trigger?.setAttribute('aria-expanded', 'false');

    if (!panel) {
      this.finishClose(menu);
      return;
    }

    panel.classList.add('menu-closing');

    const finishOnOpacity = (event) => {
      if (event.target !== panel || event.propertyName !== 'opacity') return;
      this.finishClose(menu);
    };

    panel.addEventListener('transitionend', finishOnOpacity);

    const fallbackTimer = window.setTimeout(
      () => this.finishClose(menu),
      this.closeFallbackDuration
    );

    this.closeAnimations.set(menu, {
      panel,
      finishOnOpacity,
      fallbackTimer,
    });
  }

  finishClose(menu) {
    if (!menu) return;

    this.cancelEntryFrame(menu);
    this.clearCloseAnimation(menu);

    const panel = menu.querySelector('.mega-menu__content');
    const trigger = menu.querySelector('.mega-menu__trigger');

    menu.classList.remove('is-open');
    panel?.classList.remove('menu-closing', 'animate-items', 'shine');
    trigger?.setAttribute('aria-expanded', 'false');

    if (this.currentMenu === menu) this.currentMenu = null;
  }

  scheduleClose(menu) {
    if (!menu || this.currentMenu !== menu) return;

    this.cancelScheduledClose();
    this.closeDelayTimer = window.setTimeout(() => {
      this.closeDelayTimer = null;
      this.close(menu);
    }, this.closeDelay);
  }

  cancelScheduledClose() {
    if (this.closeDelayTimer === null) return;

    clearTimeout(this.closeDelayTimer);
    this.closeDelayTimer = null;
  }

  cancelCloseAnimation(menu) {
    const animation = this.closeAnimations.get(menu);
    if (!animation) return;

    clearTimeout(animation.fallbackTimer);
    animation.panel.removeEventListener('transitionend', animation.finishOnOpacity);
    animation.panel.classList.remove('menu-closing');
    this.closeAnimations.delete(menu);

    if (this.currentMenu === menu) {
      menu.querySelector('.mega-menu__trigger')?.setAttribute('aria-expanded', 'true');
    }
  }

  clearCloseAnimation(menu) {
    const animation = this.closeAnimations.get(menu);
    if (!animation) return;

    clearTimeout(animation.fallbackTimer);
    animation.panel.removeEventListener('transitionend', animation.finishOnOpacity);
    this.closeAnimations.delete(menu);
  }

  cancelEntryFrame(menu) {
    const frame = this.entryFrames.get(menu);
    if (frame === undefined) return;

    cancelAnimationFrame(frame);
    this.entryFrames.delete(menu);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new MegaMenu();
});